// Stage movement/resizing in the overlay. Commit only keys changed by the gesture.
const EDIT_KEYS = ["x", "y", "width", "height", "fontSize", "fontSizes"];
const sameValue = (a, b) =>
  Object.is(a, b) ||
  (Array.isArray(a) &&
    Array.isArray(b) &&
    a.length === b.length &&
    a.every((value, index) => Object.is(value, b[index])));
export const createWidgetMovePreview = () => {
  let session = null;
  return {
    read(file, gestureKey, mode = "geometry") {
      if (
        !session ||
        session.gestureKey !== gestureKey ||
        session.mode !== mode
      ) {
        const temp = file.document.tempFields;
        const names = new Set([
          temp.selectedFormField?.name,
          ...(temp.selectedExtraFields || []).map((field) => field.name),
        ]);
        session = {
          mode,
          gestureKey,
          file,
          document: temp.currentDocument,
          originals: new Map(
            temp.formFields
              .filter((field) => names.has(field.name))
              .map((field) => [
                field.name,
                Object.fromEntries(EDIT_KEYS.map((key) => [key, field[key]])),
              ]),
          ),
        };
      }
      return session.file;
    },
    update(updater) {
      if (!session) return null;
      session.file =
        typeof updater === "function" ? updater(session.file) : updater;
      return session.file;
    },
    commit(setFile) {
      if (!session) return;
      const completed = session;
      session = null;
      if (completed.mode === "selection") {
        const draft = completed.file.document.tempFields;
        const selectedNames = (draft.selectedExtraFields || []).map(
          (field) => field.name,
        );
        const primaryName = draft.selectedFormField?.name;

        setFile((previous) => {
          const temp = previous?.document?.tempFields;

          if (!temp || temp.currentDocument !== completed.document) {
            return previous;
          }

          // Use current field objects to preserve their latest data.
          const fieldsByName = new Map(
            (temp.formFields || []).map((field) => [field.name, field]),
          );

          const selectedExtraFields = selectedNames
            .map((name) => fieldsByName.get(name))
            .filter(Boolean);

          const selectedFormField = fieldsByName.get(primaryName) || null;
          const previousSelection = temp.selectedExtraFields || [];

          if (
            selectedFormField === temp.selectedFormField &&
            previousSelection.length === selectedExtraFields.length &&
            previousSelection.every(
              (field, index) => field === selectedExtraFields[index],
            )
          ) {
            return previous;
          }

          return {
            ...previous,
            document: {
              ...previous.document,
              tempFields: {
                ...temp,
                selectedFormField,
                selectedExtraFields,
              },
            },
          };
        });

        return;
      }
      const positions = new Map();
      for (const field of completed.file.document.tempFields.formFields) {
        const original = completed.originals.get(field.name);
        if (!original) continue;
        const patch = {};
        for (const key of EDIT_KEYS) {
          if (!sameValue(field[key], original[key])) patch[key] = field[key];
        }
        if (Object.keys(patch).length) positions.set(field.name, patch);
      }
      if (!positions.size) return;

      setFile((previous) => {
        const temp = previous?.document?.tempFields;
        // Never apply an old drag to a different page/document/viewport.
        if (!temp || temp.currentDocument !== completed.document)
          return previous;
        const currentByName = new Map(
          temp.formFields.map((field) => [field.name, field]),
        );
        // Preserve an external geometry change (for example, undo during a drag).
        for (const [name] of positions) {
          const current = currentByName.get(name);
          const original = completed.originals.get(name);
          if (
            current &&
            EDIT_KEYS.some((key) => !sameValue(current[key], original[key]))
          )
            return previous;
        }
        const changedByName = new Map();
        const formFields = temp.formFields.map((field) => {
          const position = positions.get(field.name);
          if (!position) return field;
          const updated = { ...field, ...position };
          changedByName.set(field.name, updated);
          return updated;
        });
        if (!changedByName.size) return previous;
        return {
          ...previous,
          document: {
            ...previous.document,
            tempFields: {
              ...temp,
              formFields,
              selectedFormField:
                changedByName.get(temp.selectedFormField?.name) ||
                temp.selectedFormField,
              selectedExtraFields: (temp.selectedExtraFields || []).map(
                (field) => changedByName.get(field.name) || field,
              ),
            },
          },
        };
      });
    },
    cancel() {
      const wasActive = session !== null;
      session = null;
      return wasActive;
    },
  };
};
